import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useShowMore } from '../../components/useShowMore';
import EditUserDialog from '../../components/EditUserDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomersList = ({ users, onUpdated }) => {
    const customers = users.filter(u => u.role === 'user');
    const { visibleItems, ShowMoreButton, total, limit } = useShowMore(customers, 10, 10);
    if (customers.length === 0) {
        return (
            <div className="card-dark p-8 text-center">
                <p className="text-[#A0AAB2]">No customers yet. Create your first one above!</p>
            </div>
        );
    }
    return (
        <>
            <div className="space-y-2">
                {visibleItems.map(u => (
                    <div key={u.id} className="card-dark p-4 flex items-center justify-between" data-testid={`customer-${u.id}`}>
                        <div>
                            <p className="text-white font-medium">{u.name}</p>
                            <p className="text-xs text-[#A0AAB2]">{u.email}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs">
                                <span className="text-[#FFD700] font-mono">{(u.credits || 0).toFixed(2)} credits</span>
                                <span className="text-[#A0AAB2]">Referral code: <span className="text-[#00FFFF] font-mono">{u.referral_code || 'N/A'}</span></span>
                                {u.referral_count > 0 && (
                                    <span className="text-[#39FF14]">{u.referral_count} referrals ({(u.referral_earnings || 0).toFixed(2)} earned)</span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" className="text-xs border-white/10 text-[#A0AAB2] hover:text-white h-7"
                                onClick={() => {
                                    navigator.clipboard.writeText(u.referral_code || '');
                                    toast.success('Referral code copied!');
                                }}>
                                Copy Code
                            </Button>
                            <EditUserDialog user={u} onUpdated={onUpdated} size="sm" />
                        </div>
                    </div>
                ))}
            </div>
            <p className="text-[10px] text-[#A0AAB2] text-center mt-2">
                Showing {Math.min(limit, total)} of {total}
            </p>
            <ShowMoreButton testid="customers-show-more" />
        </>
    );
};

export const HierarchyAdminTab = ({ users, fetchData }) => (
    <div className="space-y-6">
        <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#00FFFF]" />
                Create Customer Account
            </h3>
        </div>

        <div className="card-dark p-6">
            <form onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const name = form.custName.value.trim();
                const email = form.custEmail.value.trim();
                const password = form.custPass.value;
                const credits = parseFloat(form.custCredits.value) || 0;
                const refCode = form.custRefCode.value.trim();
                const phone = form.custPhone.value.trim();
                const birthday = form.custBirthday.value;
                if (!name || !email || !password) return;
                if (!phone) {
                    toast.error('Phone number is required');
                    return;
                }
                try {
                    const payload = {
                        name, email, password, phone,
                        initial_credits: credits, referral_code: refCode,
                    };
                    if (birthday) payload.birthday = birthday;
                    const { data } = await axios.post(`${API}/admin/create-customer`, payload, { withCredentials: true });
                    toast.success(`${data.message}${data.referral_applied ? ' (Referral bonus applied!)' : ''}`);
                    form.reset();
                    fetchData();
                } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to create customer');
                }
            }} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs text-[#A0AAB2] mb-1 block">Customer Name *</label>
                        <Input name="custName" placeholder="John Doe" required className="bg-[#121212] border-white/10 text-white" data-testid="create-cust-name" />
                    </div>
                    <div>
                        <label className="text-xs text-[#A0AAB2] mb-1 block">Email *</label>
                        <Input name="custEmail" type="email" placeholder="john@example.com" required className="bg-[#121212] border-white/10 text-white" data-testid="create-cust-email" />
                    </div>
                    <div>
                        <label className="text-xs text-[#A0AAB2] mb-1 block">Password *</label>
                        <Input name="custPass" type="password" placeholder="Min 6 characters" required className="bg-[#121212] border-white/10 text-white" data-testid="create-cust-password" />
                    </div>
                    <div>
                        <label className="text-xs text-[#A0AAB2] mb-1 block">Phone *</label>
                        <Input name="custPhone" type="tel" placeholder="256-555-0100" required className="bg-[#121212] border-white/10 text-white" data-testid="create-cust-phone" />
                    </div>
                    <div>
                        <label className="text-xs text-[#A0AAB2] mb-1 block">Birthday <span className="text-[10px] text-[#666]">(optional — add later for $10 birthday bonus)</span></label>
                        <Input name="custBirthday" type="date" className="bg-[#121212] border-white/10 text-white" data-testid="create-cust-birthday" />
                    </div>
                    <div>
                        <label className="text-xs text-[#A0AAB2] mb-1 block">Initial Credits</label>
                        <Input name="custCredits" type="number" placeholder="0" min="0" step="0.01" className="bg-[#121212] border-white/10 text-white" data-testid="create-cust-credits" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-xs text-[#A0AAB2] mb-1 block">Referral Code (optional)</label>
                        <Input name="custRefCode" placeholder="BET-XXXXXX" className="bg-[#121212] border-white/10 text-white" data-testid="create-cust-refcode" />
                        <p className="text-[10px] text-[#A0AAB2] mt-1">If another customer referred this person, enter their referral code. Both will receive bonus credits.</p>
                    </div>
                </div>
                <Button type="submit" className="btn-primary" data-testid="create-cust-submit">
                    <Plus className="w-4 h-4 mr-2" />Create Customer
                </Button>
            </form>
        </div>

        <div>
            <h4 className="text-sm font-bold text-[#A0AAB2] uppercase tracking-wider mb-3">All Customers</h4>
            <CustomersList users={users} onUpdated={fetchData} />
        </div>
    </div>
);
